import { useState } from "react";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

const initial = { current_password: "", new_password: "", confirm: "" };

const ChangePasswordCard = () => {
  const { logout } = useAuth();
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (data.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (data.new_password !== data.confirm) {
      toast.error("New password and confirmation don’t match.");
      return;
    }
    if (data.current_password === data.new_password) {
      toast.error("New password must be different from your current password.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: data.current_password,
        new_password: data.new_password,
      });
      toast.success("Password updated — please sign in again.");
      setData(initial);
      await logout();
      navigate("/admin/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-10 rounded-lg bg-white border border-border p-6"
      data-testid="admin-change-password"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green-100 text-crest-green">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-crest-navy">Change password</h2>
          <p className="text-xs text-muted-foreground">
            At least 8 characters. You will be signed out and asked to log in again.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 grid md:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Current password</label>
          <Input
            type="password"
            value={data.current_password}
            onChange={set("current_password")}
            className="mt-2 h-11"
            required
            autoComplete="current-password"
            data-testid="change-pw-current"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">New password</label>
          <Input
            type="password"
            value={data.new_password}
            onChange={set("new_password")}
            className="mt-2 h-11"
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="change-pw-new"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Confirm new password</label>
          <Input
            type="password"
            value={data.confirm}
            onChange={set("confirm")}
            className="mt-2 h-11"
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="change-pw-confirm"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="change-pw-submit"
          className="md:col-span-3 md:justify-self-start inline-flex items-center justify-center gap-2 rounded-md bg-crest-green px-6 py-3 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200 disabled:opacity-60 h-11"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Update password
        </button>
      </form>
    </section>
  );
};

export default ChangePasswordCard;
